import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Select, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CutJobClient, type CutJobDTO, type OrderCutJobsDTO } from "../../../api/cutJobs";
import { formatNumber, refusalMessage } from "../../../api/manufacturing";
import type { ProductionOrderDTO } from "../../../api/production";
import { CutJobDocuments } from "./CutJobDocuments";
import { CUT_JOBS_KEY, cutJobStatusLabel, cutJobStatusVariant, cutJobTitle, jobProcessLabel } from "./cutJobModel";

export const orderCutJobsKey = (orderId: number) => [...CUT_JOBS_KEY, "order", orderId];

export function useOrderCutJobs(orderId: number, enabled = true) {
  const { http } = useAuth();
  const client = useMemo(() => new CutJobClient(http), [http]);
  return useQuery({ queryKey: orderCutJobsKey(orderId), queryFn: () => client.forOrder(orderId), enabled });
}

/**
 * A lot's cut jobs (docs/cutting-jobs §6, decision 5): each job with its
 * progress and its other lots, managed from here; the lot's raw stock in no
 * job yet, to put in a new job or a draft one; its documents and its jobs'.
 * The lot is released once all its raw stock is in confirmed jobs.
 */
export function OrderCutJobsTab({ lot }: { lot: ProductionOrderDTO }) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const client = useMemo(() => new CutJobClient(http), [http]);
  const data = useOrderCutJobs(lot.id);
  const drafts = useQuery({
    queryKey: [...CUT_JOBS_KEY, "drafts"],
    queryFn: async () => (await client.findAll()).filter((job) => job.status === "DRAFT"),
    enabled: canWrite,
  });
  const [ticked, setTicked] = useState(new Set<number>());
  // A job is of one process: what is ticked must be cut at the same one (the saw, the foam cutter).
  const tickedProcesses = [...new Set(unassignedOf(data.data).filter((panel) => ticked.has(panel.virtual_part_id)).map((panel) => panel.process_code))];
  const process = tickedProcesses.length === 1 ? tickedProcesses[0] : null;
  const [target, setTarget] = useState("");
  const confirmed = lot.materials_confirmed_at != null || lot.kind === "ASSEMBLY";
  const refs = [...ticked].map((virtual_part_id) => ({ production_order_id: lot.id, virtual_part_id }));
  const done = async (jobId: number) => {
    setTicked(new Set());
    await queryClient.invalidateQueries({ queryKey: ["production"] });
    void navigate({ to: "/production/cut-jobs/$jobId", params: { jobId: String(jobId) } });
  };
  const fail = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
  const create = useMutation({ mutationFn: () => client.createJob(refs, null, process ?? "CUT"), onSuccess: (detail) => done(detail.job.id), onError: fail });
  const add = useMutation({ mutationFn: () => client.addPanels(Number(target), refs), onSuccess: (detail) => done(detail.job.id), onError: fail });

  if (!data.data) {
    return <p className="p-4 text-[13px] text-ink-muted lg:p-6">{t("table.loading", "Loading")}</p>;
  }
  const { jobs, unassigned, documents } = data.data;
  return (
    <div className="flex flex-col gap-6 p-4 lg:p-6">
      <section>
        <h3 className="mb-2 text-[14px] font-semibold">{t("cut-job.of-lot", "This lot's cut jobs")}</h3>
        {jobs.length === 0 ? (
          <p className="text-[13px] text-ink-muted">{t("cut-job.of-lot.none", "In no cut job yet.")}</p>
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line text-[13px]">
            {jobs.map((job) => (
              <JobRow key={job.id} job={job} lotNumber={lot.number} />
            ))}
          </ul>
        )}
      </section>

      {unassigned.length > 0 && (
        <section>
          <h3 className="mb-1 text-[14px] font-semibold">{t("cut-job.unassigned", "Raw panels in no job")}</h3>
          <p className="mb-2 text-[12px] text-ink-muted">
            {confirmed
              ? t("cut-job.unassigned.hint", "Put each in a cut job, as Cut Rite batched it, before the lot is released.")
              : t("cut-job.unassigned.confirm-first", "Confirm the production BOM first: the jobs take their panels from it.")}
          </p>
          <ul className="flex flex-col gap-1 rounded-md border border-line p-2 text-[13px]">
            {unassigned.map((panel) => (
              <label key={panel.virtual_part_id} className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--color-accent)]"
                  disabled={!canWrite || !confirmed}
                  checked={ticked.has(panel.virtual_part_id)}
                  onChange={(event) => {
                    const next = new Set(ticked);
                    if (event.target.checked) next.add(panel.virtual_part_id);
                    else next.delete(panel.virtual_part_id);
                    setTicked(next);
                  }}
                />
                <span className="font-mono">{panel.virtual_number}</span>
                <Badge size="sm" variant="neutral">{jobProcessLabel(panel.process_code, t)}</Badge>
                <span className="min-w-0 flex-1 truncate text-ink-muted">{panel.planned}</span>
              </label>
            ))}
          </ul>
          {canWrite && confirmed && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {tickedProcesses.length > 1 && (
                <span className="text-[12px] text-warn">{t("cut-job.one-process", "A job is cut at one place: tick the saw's or the foam's.")}</span>
              )}
              <Button size="sm" variant="primary" disabled={ticked.size === 0 || process == null} loading={create.isPending} onClick={() => create.mutate()}>
                {t("cut-job.create-with", "New job with these")}
              </Button>
              {(drafts.data ?? []).length > 0 && (
                <>
                  <span className="text-[12px] text-ink-muted">{t("cut-job.or", "or")}</span>
                  <Select
                    className="w-56"
                    placeholder={t("cut-job.pick-draft", "A draft job")}
                    value={target}
                    onChange={(event) => setTarget(event.target.value)}
                    options={(drafts.data ?? []).filter((job) => process == null || job.process_code === process)
                      .map((job) => ({ value: String(job.id), label: `${cutJobTitle(job, t)} · ${job.lots ?? ""}` }))}
                  />
                  <Button size="sm" variant="secondary" disabled={ticked.size === 0 || target === "" || process == null} loading={add.isPending} onClick={() => add.mutate()}>
                    {t("cut-job.add-to", "Add to it")}
                  </Button>
                </>
              )}
            </div>
          )}
        </section>
      )}

      <section>
        <h3 className="mb-2 text-[14px] font-semibold">{t("cut-job.documents", "Documents")}</h3>
        <CutJobDocuments documents={documents} owner={{ productionOrderId: lot.id }} />
      </section>
    </div>
  );
}

function unassignedOf(data: OrderCutJobsDTO | undefined) {
  return data?.unassigned ?? [];
}

function JobRow({ job, lotNumber }: { job: CutJobDTO; lotNumber: number }) {
  const { t } = useTranslation();
  const others = (job.lots ?? "").split(",").map((one) => one.trim()).filter((one) => one !== "" && one !== String(lotNumber));
  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2">
      <Link to="/production/cut-jobs/$jobId" params={{ jobId: String(job.id) }} className="font-semibold text-accent hover:underline">
        {cutJobTitle(job, t)}
      </Link>
      <Badge size="sm" variant={cutJobStatusVariant(job.status)}>{cutJobStatusLabel(job.status, t)}</Badge>
      <span className="font-mono text-ink-muted">{job.panels}</span>
      <span className="tabular-nums">
        {t("cut-job.progress", "{{done}}/{{required}} pieces · {{used}}/{{planned}} sheets", {
          done: formatNumber(job.pieces_done ?? 0),
          required: formatNumber(job.pieces_required ?? 0),
          used: formatNumber(job.sheets_used ?? 0),
          planned: formatNumber(job.sheets_planned ?? 0),
        })}
      </span>
      {others.length > 0 && (
        <span className="text-ink-muted">{t("cut-job.with-lots", "with lots {{list}}", { list: others.join(", ") })}</span>
      )}
      {(job.document_count ?? 0) > 0 && <Badge size="sm" variant="neutral">{t("cut-job.doc-count", "{{n}} documents", { n: job.document_count })}</Badge>}
    </li>
  );
}

/** On the lot's Floor tab: the jobs its pieces are cut in, with their progress. */
export function CutJobStrip({ lot }: { lot: ProductionOrderDTO }) {
  const { t } = useTranslation();
  const data = useOrderCutJobs(lot.id, !lot.is_from_sigm);
  const jobs = data.data?.jobs ?? [];
  if (jobs.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-line bg-surface-sunk px-4 py-2 text-[13px] lg:px-6">
      <span className="text-ink-muted">{t("cut-job.cut-in", "Cut in")}</span>
      {jobs.map((job) => (
        <span key={job.id} className="inline-flex items-center gap-2">
          <Link to="/production/cut-jobs/$jobId" params={{ jobId: String(job.id) }} className="font-semibold text-accent hover:underline">
            {cutJobTitle(job, t)}
          </Link>
          <Badge size="sm" variant={cutJobStatusVariant(job.status)}>{cutJobStatusLabel(job.status, t)}</Badge>
          <span className="tabular-nums text-ink-muted">
            {formatNumber(job.pieces_done ?? 0)}/{formatNumber(job.pieces_required ?? 0)}
          </span>
        </span>
      ))}
    </div>
  );
}
